import React from "react";
import { AxisTop } from "@visx/axis";
import { GridColumns } from "@visx/grid";
import { Group } from "@visx/group";
import { scaleLinear } from "@visx/scale";

import { Bar, BarData } from "./bar";
import "./bar_chart.scss";

/*
 * Types
 */

export type LabelPosition = "top" | "on";

export interface BarChartProps {
    readonly data: ReadonlyArray<BarData>;
    readonly maxValue?: number;
    readonly isRaw?: boolean;
    readonly backgroundColor?: string;
    readonly labelPosition?: LabelPosition;
}

/*
 * Main
 */

let height;
const width = 720;

export const BarChart = (props: BarChartProps): React.ReactElement => {
    const { data, maxValue, isRaw, backgroundColor, labelPosition = "on" } = props;
    const marginTop = labelPosition === "top" ? 28 : 19;

    const xScale = scaleLinear({
        domain: [0, maxValue ?? 100],
        range: [0, width],
    });

    height = 94;
    if (data.length > 2) {
        for (let i = 2; i < data.length; i++) {
            height += labelPosition === "top" ? 85 : 35;
        }
    }

    const getColor = (label: string) => {
        if (label.includes("Benchmark")) {
            return backgroundColor ? darkenColor(backgroundColor, 0.45) : "#0275bb";
        } else {
            return backgroundColor ?? "#00a3e0";
        }
    };

    function darkenColor(hexColor: string, darkenPercent: number) {
        const hex = hexColor.replace("#", "");
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        const dr = Math.max(0, r - r * darkenPercent);
        const dg = Math.max(0, g - g * darkenPercent);
        const db = Math.max(0, b - b * darkenPercent);

        const hr = Math.round(dr).toString(16).padStart(2, "0");
        const hg = Math.round(dg).toString(16).padStart(2, "0");
        const hb = Math.round(db).toString(16).padStart(2, "0");

        return `#${hr}${hg}${hb}`;
    }

    return (
        <div className="bar-chart__wrapper">
            <svg fill="#fff" height={height} width={width}>
                <Group top={marginTop}>
                    <AxisTop
                        hideAxisLine
                        hideTicks
                        hideZero
                        scale={xScale}
                        tickFormat={(p): string => (isRaw ? `${p}` : `${p}%`)}
                        tickLabelProps={(
                            _value,
                            index,
                        ): {
                            fontFamily: string;
                            dx: number;
                            fontSize: string;
                            fill: string;
                            fontWeight: number;
                        } => ({
                            dx: index !== 10 ? -30 : -37,
                            fill: "var(--color-grey-3)",
                            fontSize: "12px",
                            fontFamily: "Helvetica",
                            fontWeight: 700,
                        })}
                    />
                    <GridColumns
                        height={height}
                        lineStyle={{ pointerEvents: "none" }}
                        scale={xScale}
                        stroke="#f1f1f1"
                        strokeWidth={2}
                        top={-marginTop}
                    />
                    {data.map((d, i) => {
                        const barWidth = xScale(d.value > 100 ? 100 : d.value) || 10;
                        const barHeight = 26; // yScale.bandwidth() for 3 bars
                        const pixelX = 0;
                        const spacing = labelPosition === "top" ? 40 : 9;
                        const pixelY = (barHeight + spacing) * i;

                        return (
                            <Bar
                                color={getColor(d.label)}
                                data-label={d.label}
                                data-percentage={d.value}
                                height={barHeight}
                                key={`bar-${d.label}`}
                                label={d.label}
                                labelPosition={labelPosition}
                                value={d.value}
                                width={barWidth}
                                x={pixelX}
                                y={pixelY}
                            />
                        );
                    })}
                </Group>
            </svg>
        </div>
    );
};
